import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { CircularProgress } from 'src/components/CircularProgress';
import { Divider } from 'src/components/Divider';
import { FormControlLabel } from 'src/components/FormControlLabel';
import { Grid } from 'src/components/Grid';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { ServiceResourceRule } from 'src/models/appointment/_types';
import { fetchParentResources } from 'src/models/fbm-resource/fbm-resource';
import { FeatureFlag } from 'src/models/platform/_types';
import { EditEventFormValues } from 'src/pages/Schedule/Calendar/EditEventDialog/_types';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';


import type { Service } from 'src/models/service/_types';
import type { Identifier } from 'src/types/utilityTypes';

interface ResourceSettingsSectionProps {
  getServiceById: (serviceId: Identifier | null) => Service | undefined;
}

export const ResourceSettingsSection: FC<ResourceSettingsSectionProps> = ({
  getServiceById,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'calendar.editEventDialog.resourceSettings' });
  const { session } = useSession();
  const { values, setFieldValue } = useFormikContext<EditEventFormValues>();

  const USAGE_BEHAVIOR_OPTIONS = [
    {
      id: 'all_resources',
      label: t('noParentAtCapacity'),
    },
    {
      id: 'session',
      label: t('yesEachSessionUsesSubAsset'),
    },
    {
      id: 'visit',
      label: t('yesEachAttendeeUsesSubAsset'),
    },
  ];

  // Check if feature flag is enabled and location is selected
  const showResources =
    hasFeatureFlag(session, FeatureFlag.ResourceManagement) &&
    !!values.locationId;

  // Fetch parent resources for the selected location
  const { data: parentResources = [], isLoading: resourcesLoading } = useQuery({
    queryKey: ['parentResources', values.locationId],
    queryFn: () =>
      fetchParentResources({
        location_id: values.locationId,
        'q[parent_id_null]': true, // Only get parent resources
      }),
    enabled: showResources && !!values.locationId,
    staleTime: 60000,
  });

  // Get service resource rules for "copy from service" functionality
  const selectedService = getServiceById(values.serviceId);

  // Parent resource options for select
  const parentResourceOptions = parentResources.map((r) => ({
    id: r.id,
    label: r.name,
  }));

  // Get sub-resources for a parent resource
  const getSubResourceOptions = (parentId: Identifier | null) => {
    if (!parentId) return [];
    const parent = parentResources.find((r) => r.id === parentId);
    return (
      parent?.subResources?.map((sub) => ({
        id: sub.id,
        label: sub.name,
      })) || []
    );
  };

  // Add a new resource rule
  const handleAddResource = () => {
    const newRule: ServiceResourceRule = {
      resourceId: null,
      usageBehavior: 'all_resources',
      subResourceIds: [],
    };
    setFieldValue('serviceResourceRules', [
      ...values.serviceResourceRules,
      newRule,
    ]);
  };

  // Remove a resource rule
  const handleRemoveResource = (index: number) => {
    const newRules = values.serviceResourceRules.filter((_, i) => i !== index);
    setFieldValue('serviceResourceRules', newRules);
  };

  // Update a specific rule field
  const handleRuleChange = (
    index: number,
    field: keyof ServiceResourceRule,
    value: unknown
  ) => {
    const newRules = [...values.serviceResourceRules];
    newRules[index] = { ...newRules[index], [field]: value };

    // Reset sub-resources when parent changes
    if (field === 'resourceId') {
      newRules[index].subResourceIds = [];
    }

    setFieldValue('serviceResourceRules', newRules);
  };

  // Copy resource rules from service
  const handleCopyFromService = () => {
    if (!selectedService?.serviceResources?.length) return;

    const copiedRules: ServiceResourceRule[] =
      selectedService.serviceResources.map((sr) => ({
        resourceId: sr.resource?.id || null,
        usageBehavior: sr.usageBehavior || 'all',
        subResourceIds: sr.subResourceIds || [],
      }));

    setFieldValue('serviceResourceRules', copiedRules);
  };

  // Toggle disable resources
  const handleNoResourcesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFieldValue('noResources', event.target.checked);
  };

  if (!showResources) {
    return null;
  }

  return (
    <Box
      component="fieldset"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        m: 0,
      }}
    >
      <Typography variant="h6" component="legend">
        {t('title')}
        {selectedService?.serviceResources?.length ? (
          <Link
            component="button"
            type="button"
            onClick={handleCopyFromService}
            sx={{ ml: 1, fontSize: '0.875rem' }}
          >
            {t('copyFromService')}
          </Link>
        ) : null}
      </Typography>

      {resourcesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size="small" />
        </Box>
      ) : (
        <Stack spacing={2}>
          {values.serviceResourceRules.map((rule, index) => {
            const subResourceOptions = getSubResourceOptions(rule.resourceId);
            const hasSubResources = subResourceOptions.length > 0;

            return (
              <Box key={index}>
                <Grid container spacing={2} alignItems="flex-start">
                  {/* Parent Resource Select */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <BaseSelect
                          label={t('selectAsset')}
                          placeholder={t('selectParentAsset')}
                          options={parentResourceOptions}
                          value={rule.resourceId}
                          onChange={(value) =>
                            handleRuleChange(
                              index,
                              'resourceId',
                              value as Identifier | null
                            )
                          }
                        />
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveResource(index)}
                      >
                        <Icon
                          icon={faTrash}
                          size="xsmall"
                        />
                      </IconButton>
                    </Box>
                  </Grid>

                  {/* Usage Behavior Select */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <BaseSelect
                      label={t('allowOtherBookings')}
                      options={USAGE_BEHAVIOR_OPTIONS}
                      value={rule.usageBehavior}
                      onChange={(value) =>
                        handleRuleChange(
                          index,
                          'usageBehavior',
                          value as string
                        )
                      }
                    />
                  </Grid>

                  {/* Sub-Resource Multi-Select (if parent has sub-resources) - on its own row */}
                  {hasSubResources && (
                    <Grid size={{ xs: 12 }}>
                      <BaseSelect
                        label={t('selectSubAsset')}
                        placeholder={t('defaultAll')}
                        options={subResourceOptions}
                        value={rule.subResourceIds || null}
                        onChange={(value) =>
                          handleRuleChange(
                            index,
                            'subResourceIds',
                            value as Identifier[]
                          )
                        }
                        multiple
                      />
                    </Grid>
                  )}
                </Grid>

                {index < values.serviceResourceRules.length - 1 && (
                  <Divider sx={{ mt: 2 }} />
                )}
              </Box>
            );
          })}

          {/* Add Resource Link */}
          <Box>
            <Link
              component="button"
              type="button"
              onClick={handleAddResource}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Icon
                icon={faPlus}
                size="xsmall"
              />
              {t('addAsset')}
            </Link>
          </Box>

          {/* Disable Resources Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={values.noResources}
                onChange={handleNoResourcesChange}
              />
            }
            label={t('disableAssets')}
          />
        </Stack>
      )}
    </Box>
  );
};
